import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Trophy, MapPin, Users, TrendingUp, Heart, Star, ChevronRight, Building2, Quote } from 'lucide-react';

const milestones = [
  {
    year: '2012',
    icon: Star,
    color: '#EA723D',
    title: 'La naissance',
    subtitle: 'Deux frères, un rêve commun',
    text: 'Le groupe Borbiconi Immobilier voit le jour après une carrière passionnante dans le football professionnel. La rigueur du sport de haut niveau rencontre l\'exigence de l\'immobilier.',
    image: '/histoire-stade.jpg',
    imageAlt: 'Stade de football au crépuscule',
  },
  {
    year: '2014',
    icon: Trophy,
    color: '#EA723D',
    title: 'Marchands de biens',
    subtitle: 'L\'école du terrain',
    text: 'Avant l\'agence, l\'expertise s\'est forgée sur le terrain en tant que marchands de biens : achat, valorisation, revente. Une formation immersive qui offre une connaissance intime du marché local.',
    image: null,
  },
  {
    year: '2015',
    icon: MapPin,
    color: '#EA723D',
    title: 'Cap sur le Luxembourg',
    subtitle: 'L\'ouverture transfrontalière',
    text: 'Face à une demande croissante de clients vendant au Luxembourg pour acquérir en France — et inversement — l\'agence ouvre une structure au Grand-Duché pour offrir une continuité de service.',
    image: '/histoire-region.jpg',
    imageAlt: 'Paysage aérien de la région frontalière franco-luxembourgeoise',
  },
  {
    year: '2024',
    icon: TrendingUp,
    color: '#EA723D',
    title: 'La Meuse nous appelle',
    subtitle: 'Un marché en pleine évolution',
    text: 'De nombreux actifs travaillant au Luxembourg recherchent des biens plus accessibles à 1h-1h30, des résidences spacieuses ou des maisons secondaires dans un cadre naturel. Borbiconi répond présent.',
    image: '/histoire-meuse.jpg',
    imageAlt: 'Vallée de la Meuse, paysage rural français',
  },
  {
    year: 'Aujourd\'hui',
    icon: Users,
    color: '#EA723D',
    title: 'Un réseau d\'experts',
    subtitle: 'Du Luxembourg jusqu\'à la Meuse',
    text: 'Grâce à ses collaborateurs, l\'agence couvre désormais un territoire étendu : Ottange, l\'axe Thionville-Metz, Longwy, le Val de Briey, Mercy-le-Bas, jusqu\'à la Meuse. La proximité, partout.',
    image: null,
  },
];

const values = [
  { img: '/steps/step1.png', label: 'Écoute', desc: 'Chaque projet est unique. Nous prenons le temps de comprendre vos besoins avant d\'agir.' },
  { img: '/steps/step2.png', label: 'Transparence', desc: 'Aucune surprise. Des informations claires, des engagements tenus.' },
  { img: '/steps/step3.png', label: 'Rigueur', desc: 'Héritée du sport de haut niveau : exigence, discipline et dépassement de soi.' },
  { img: '/steps/step7.png', label: 'Proximité', desc: 'Des experts présents sur le terrain, connaissant chaque rue, chaque quartier.' },
];

const TimelineItem = ({ milestone, index }) => {
  const Icon = milestone.icon;
  const isEven = index % 2 === 0;

  return (
    <div className={`relative flex items-start gap-0 md:gap-8 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
      {/* Content card */}
      <motion.div
        initial={{ opacity: 0, x: isEven ? -40 : 40 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`w-full md:w-[calc(50%-2.5rem)] ${isEven ? 'md:text-right' : 'md:text-left'} pl-14 md:pl-0`}
      >
        <div className="group bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border border-white/10 hover:border-[#EA723D]/30 transition-all duration-500 hover:-translate-y-1 overflow-hidden break-words">
          {/* Image optionnelle */}
          {milestone.image && (
            <div className="h-44 overflow-hidden">
              <img
                src={milestone.image}
                alt={milestone.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 opacity-80"
              />
            </div>
          )}
          <div className="p-7 relative">
            <div className={`flex flex-col sm:flex-row sm:items-center gap-4 mb-4 ${isEven ? 'md:flex-row-reverse' : ''}`}>
              <span
                className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-none whitespace-nowrap"
                style={{ color: milestone.color, opacity: 0.15, fontFamily: 'Georgia, serif' }}
              >
                {milestone.year}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-black tracking-widest uppercase text-white/30">{milestone.year}</p>
                <h3 className="text-xl font-serif text-white leading-tight">{milestone.title}</h3>
              </div>
            </div>
            <p className="text-[#EA723D] text-[10px] font-bold uppercase tracking-widest mb-3">{milestone.subtitle}</p>
            <p className="text-white/60 text-sm leading-relaxed">{milestone.text}</p>
          </div>
        </div>
      </motion.div>

      {/* Center Icon node */}
      <div className="hidden md:flex flex-col items-center w-20 flex-shrink-0">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2, type: 'spring' }}
          className="w-16 h-16 rounded-full border-4 border-[#0f0f16] shadow-lg flex items-center justify-center z-10 relative"
          style={{ backgroundColor: milestone.color }}
        >
          <Icon size={22} className="text-white" />
        </motion.div>
      </div>

      {/* Mobile icon dot */}
      <div
        className="md:hidden absolute left-0 top-[3.5rem] w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-[#0f0f16] z-10"
        style={{ backgroundColor: milestone.color }}
      >
        <Icon size={14} className="text-white" />
      </div>

      {/* Spacer */}
      <div className="hidden md:block w-[calc(50%-2.5rem)]" />
    </div>
  );
};

const Histoire = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // Animation plume
  const quoteText = "Nous ne vendons pas des maisons, nous accompagnons des projets de vie. Notre réussite se mesure à la satisfaction de nos clients et à la pérennité des relations que nous tissons avec eux.";
  const words = quoteText.split(" ");
  
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { 
        staggerChildren: 0.1, // Ralenti (0.1s par mot au lieu de 0.04s)
        delayChildren: 0.3 
      },
    },
  };

  const child = {
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
    hidden: {
      opacity: 0,
      y: 5,
    },
  };

  return (
    <div className="bg-white min-h-screen overflow-x-hidden">

      {/* ─── HERO ULTRA-MODERNE (VIDÉO & DÉGRADÉ) ─── */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0d0d12]">
        {/* Background Vidéo */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0d0d12]/40 to-white z-20" />
          <motion.div 
            style={{ y: heroY }}
            className="absolute inset-0 z-10 overflow-hidden" 
          >
            <video
              autoPlay
              muted
              playsInline
              preload="auto"
              className="w-full h-full object-cover opacity-60 scale-105"
            >
              <source src="/Du-Foot-a-l-immo.mp4" type="video/mp4" />
            </video>
          </motion.div>
          
          {/* Formes lumineuses animées */}
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              x: [0, 40, 0],
              y: [0, 20, 0]
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            className="absolute top-1/4 -right-10 w-[500px] h-[500px] bg-[#EA723D]/15 rounded-full blur-[100px] mix-blend-screen pointer-events-none z-15"
          />
        </div>

        <div className="relative z-30 max-w-7xl mx-auto px-6 lg:px-12 text-center py-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-10 inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md"
          >
            <span className="w-2 h-2 rounded-full bg-[#EA723D] animate-pulse" />
            <span className="text-white/60 text-[10px] uppercase tracking-[0.4em] font-bold">L'Élite de l'Immobilier depuis 2012</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-4xl md:text-6xl lg:text-7xl font-serif mb-10 leading-[1.05]"
          >
            <span className="text-white block">Du terrain de sport</span>
            <span className="text-[#EA723D] italic block">au terrain immobilier</span>
          </motion.h1>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2,
                  delayChildren: 0.8
                }
              }
            }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 max-w-5xl mx-auto mt-20 relative z-40"
          >
            {[
              { value: '2012', label: 'Fondation' },
              { value: '2 Pays', label: 'France / Luxembourg' },
              { value: '8', label: 'Experts Passionnés' },
            ].map((stat, i) => (
              <motion.div 
                key={i}
                variants={{
                  hidden: { opacity: 0, y: 30, scale: 0.8, filter: 'blur(10px)' },
                  visible: { 
                    opacity: 1, 
                    y: 0, 
                    scale: 1, 
                    filter: 'blur(0px)',
                    transition: { type: 'spring', stiffness: 100, damping: 12 } 
                  }
                }}
                className="flex flex-col items-center group relative p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 bg-white/5 backdrop-blur-2xl border border-white/10 shadow-2xl hover:bg-white/10 hover:border-[#EA723D]/30"
              >
                <span className="text-4xl md:text-5xl font-black text-white mb-2 group-hover:text-[#EA723D] transition-colors tracking-tighter drop-shadow-md whitespace-nowrap">{stat.value}</span>
                <span className="text-[10px] uppercase tracking-[0.4em] text-[#EA723D]/80 font-black group-hover:text-[#EA723D]">{stat.label}</span>
                
                <div className="absolute inset-0 rounded-3xl bg-[#EA723D]/5 opacity-0 group-hover:opacity-100 transition-opacity blur-2xl -z-10" />
              </motion.div>
            ))}
          </motion.div>
        </div>


      </section>

      {/* ─── INTRO NARRATIVE (STYLE SOMBRE) ─── */}
      <section className="py-24 md:py-40 relative overflow-hidden bg-[#0f0f16]">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#EA723D]/5 rounded-full blur-[100px] -translate-y-1/2 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Left: Story */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[#EA723D] text-[10px] font-black tracking-[0.5em] uppercase mb-8">Le point de départ</p>
              <h2 className="text-3xl md:text-5xl font-normal text-white mb-10 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Un parcours atypique,<br />
                <span className="italic" style={{ color: '#EA723D' }}>une vision unique</span>
              </h2>
              <div className="space-y-6 text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
                <p>
                  Christophe Borbiconi a vécu une première vie passionnante dans le football professionnel — en France comme à l'étranger. Ce métier exigeant lui a inculqué des qualités rares : rigueur, détermination et esprit d'équipe.
                </p>
                <p>
                  Au fil de ses expériences, il a développé un intérêt profond pour l'immobilier. À l'issue de sa carrière sportive, il s'est lancé comme marchand de biens, avant de créer Borbiconi Immobilier pour accompagner pleinement vendeurs, acquéreurs, bailleurs et locataires.
                </p>
                <p>
                  Aujourd'hui, l'agence est fidèle à ses origines : une aventure familiale, humaine, portée par des valeurs qui ne se négocient pas.
                </p>
              </div>
            </motion.div>

            {/* Right: Photo */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]">
                <img
                  src="/histoire.jpg"
                  alt="Christophe Borbiconi"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0f0f16]/90 to-transparent p-6">
                  <p className="text-white font-serif text-lg">Christophe Borbiconi</p>
                  <p className="text-[#EA723D] text-[10px] font-bold uppercase tracking-widest mt-1">Fondateur · Depuis 2012</p>
                </div>
              </div>

              {/* Citation flottante */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="absolute -bottom-10 -right-6 rounded-3xl p-8 max-w-xs hidden md:block border bg-white/5 backdrop-blur-3xl border-[#EA723D]/25 shadow-2xl"
              >
                <Quote size={20} className="text-[#EA723D] mb-4" />
                <p className="text-white text-sm font-serif italic leading-relaxed">
                  "La rigueur du sport, nous l'appliquons chaque jour au service de vos projets."
                </p>

              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── BLOC CITATION PLUME (RESTORED & VISIBLE) ─── */}
      <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden border-y border-gray-100">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <Quote size={30} className="text-[#EA723D]/20 mx-auto mb-10" />
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-xl md:text-2xl font-normal leading-relaxed text-[#2C353D] font-serif">
              {quoteText}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.5 }}
            className="mt-14 flex flex-col items-center"
          >
            <div className="w-12 h-[1px] bg-[#EA723D]/30 mb-6" />
            <p className="text-[#EA723D] text-[10px] font-black tracking-[0.3em] uppercase">Borbiconi Immobilier · Depuis 2012</p>
          </motion.div>
        </div>
      </section>

      {/* ═══ TIMELINE (SOMBRE — STYLE PORTAIL) ═══ */}
      <section className="py-24 md:py-40 bg-[#0f0f16] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'radial-gradient(circle, #EA723D 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-20 md:mb-32"
          >
            <p className="text-[#EA723D] text-[10px] font-black tracking-[0.5em] uppercase mb-6">Notre évolution</p>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-normal text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Notre parcours,<br />
              <span className="italic" style={{ color: '#EA723D' }}>les grandes étapes</span>
            </h2>
            <div className="w-20 h-[1px] bg-[#EA723D]/50 mx-auto mt-10" />
          </motion.div>

          <div className="relative">
            {/* Ligne verticale centrale stylisée */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-[1px] bg-gradient-to-b from-[#EA723D]/50 via-white/10 to-transparent -translate-x-1/2" />
            
            <div className="flex flex-col gap-12 md:gap-24 relative z-10">
              {milestones.map((milestone, index) => (
                <TimelineItem key={index} milestone={milestone} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TERRITOIRE ═══ */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <p className="text-[#EA723D] text-xs font-black tracking-[0.5em] uppercase mb-4">Notre territoire</p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#2C353D] leading-tight max-w-3xl mx-auto">
              Du Luxembourg à la Meuse,<br />
              <span className="text-[#EA723D] italic">votre marché, notre terrain</span>
            </h2>
          </motion.div>

          {/* Grille des 4 régions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { name: 'Luxembourg', img: '/luxembourg.png', desc: 'Le Grand-Duché' },
              { name: 'Moselle', img: '/moselle.png', desc: 'Grande Frontière, Thionville et Metz' },
              { name: 'Meurthe-et-Moselle', img: '/meurthe-et-moselle.png', desc: 'Longwy & Val de Briey' },
              { name: 'Meuse', img: '/meuse.png', desc: 'VAL DUNOIS et Verdun' },
            ].map((region, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative rounded-2xl overflow-hidden aspect-[4/5] shadow-lg"
              >
                <img
                  src={region.img}
                  alt={region.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C353D]/90 via-[#2C353D]/20 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-white font-bold text-xl mb-1">{region.name}</h3>
                  <p className="text-[#EA723D] text-[10px] font-black uppercase tracking-widest">{region.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="text-center mt-16"
          >
            <p className="text-[#2C353D] text-lg md:text-2xl font-serif max-w-4xl mx-auto leading-relaxed italic">
              "Depuis 2012, nos experts sillonnent ces territoires pour offrir à chaque client une expertise locale unique."
            </p>
            <div className="w-12 h-[2px] bg-[#EA723D] mx-auto mt-8" />
          </motion.div>
        </div>
      </section>

      {/* ═══ VALUES ═══ */}
      <section className="py-24 md:py-32 bg-[#2C353D] relative overflow-hidden text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-[#EA723D] text-xs font-black tracking-[0.5em] uppercase mb-4">Notre ADN</p>
          <h2 className="text-3xl md:text-4xl font-serif mb-16">Les valeurs qui nous définissent</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group flex flex-col bg-white/5 border border-white/10 rounded-3xl overflow-hidden hover:bg-white/10 hover:border-[#EA723D]/30 transition-all duration-500 hover:-translate-y-2"
              >
                {/* Illustration container */}
                <div className="w-full h-56 bg-[#f0e8de]/10 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[#2C353D]/20 mix-blend-multiply group-hover:bg-transparent transition-colors duration-500 z-10" />
                  <img 
                    src={value.img} 
                    alt={value.label} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-90"
                  />
                  {/* Decorative line */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#EA723D] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20" />
                </div>
                
                {/* Text content */}
                <div className="p-8 text-center flex-1 flex flex-col justify-center">
                  <h3 className="text-2xl font-serif mb-4 text-white group-hover:text-[#EA723D] transition-colors">{value.label}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{value.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-24 md:py-32 bg-white text-center">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-[#EA723D] text-xs font-black tracking-[0.5em] uppercase mb-6">Rejoignez l'aventure</p>
          <h2 className="text-3xl md:text-5xl font-serif text-[#2C353D] mb-12 italic">Prêt à confier votre projet ?</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#/contact" className="px-8 py-4 rounded-full bg-[#EA723D] text-white font-bold text-sm shadow-lg shadow-[#EA723D]/20">Nous contacter</a>
            <a href="#/estimation" className="px-8 py-4 rounded-full border border-[#2C353D] text-[#2C353D] font-bold text-sm">Estimer mon bien</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Histoire;
